// The shared kind-driven answer render (docs/design/slice3-knowledge-ux.md §2.4). One
// component, one branch on `kind`, so the three AskCrewAnswer shapes (answer/abstain/
// feature_request) cannot drift into ad hoc message types. `answer` is populated on
// every kind (packages/shared/src/ports/knowledge.ts:123-135) - no kind ever needs
// invented "no answer" copy.
import { useId } from "react";
import type { Citation, AskCrewAnswer, DetectedLanguage } from "@nightcrew/shared";
import glimIllustration from "../assets/glim.svg";

const LANGUAGE_NAMES: Record<DetectedLanguage, string> = {
  en: "English",
  nl: "Dutch",
  fr: "French",
  de: "German",
  es: "Spanish",
  it: "Italian",
  pt: "Portuguese",
  pl: "Polish",
};

/** A dashed-outline icon distinct from the risk-severity glyph shape (§2.4.4). */
const AssumingIcon = (): React.JSX.Element => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeDasharray="3 3"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
  </svg>
);

/**
 * W2 fix (design-review): a distinct icon for the abstain flag - a solid-outline
 * circle with a vertical bar, unlike AssumingIcon's dashed ring above, so the two
 * never look interchangeable.
 */
const AbstainIcon = (): React.JSX.Element => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="7.5" x2="12" y2="13" />
    <circle cx="12" cy="16.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Citation chips (§2.4.2-3): a labelled list, never inline superscripts (no offset
 * field exists on the wire to anchor one to, §7 blocker fix). Each citation is a
 * plain, always-visible list item (never a hover-only tooltip); the accessible name
 * and the visible text are the same `label ?? ref` fallback (`ref` is always
 * present). `nodeId` is captured but inert this slice - no graph destination exists
 * yet, so the chip is deliberately not a button or a link.
 */
const CitationList = ({
  citations,
  heading,
}: {
  readonly citations: readonly Citation[];
  readonly heading: string;
}): React.JSX.Element | null => {
  const labelId = useId();
  if (citations.length === 0) return null;
  return (
    <div className="msg-sources">
      <p id={labelId} className="muted" style={{ fontSize: 11, fontWeight: 600, margin: 0 }}>
        {heading}
      </p>
      <ul
        aria-labelledby={labelId}
        className="row wrap-g gap8 mt8"
        style={{ margin: 0, padding: 0, listStyle: "none" }}
      >
        {citations.map((citation, index) => (
          <li key={`${citation.ref}-${String(index)}`} className="chip" title={citation.ref}>
            {citation.label ?? citation.ref}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const CrewAnswer = ({
  answer,
  isSolo,
  onOpenFeatureLane,
  onAskAboutUpgrading,
}: {
  readonly answer: AskCrewAnswer;
  /**
   * Whether this account reads as Solo, for CTA COPY only (§2.7) - the entitlement
   * gate itself stays server-side (D36) regardless of this value.
   *
   * VERIFIED GAP (flagged in the dispatch return, not silently resolved): the ux spec
   * says this reads from `Account.planId`, but `planId` is a bare UUID
   * (packages/shared/src/model/identity.ts:14, `PlanId = z.uuid().brand...`), not a
   * tier name - the tier lives on the JOINED `Plan.name` (billing.ts:9-11), resolved
   * server-side only (apps/api/src/apps/entitlement.ts:38 `.innerJoin(plans, ...)`).
   * The session response the client actually holds (`ActiveAccountResponse`,
   * src/api/session.ts:13-16) carries no plan name today. The caller (AskCrew.tsx)
   * therefore cannot truly resolve this and passes a placeholder until either apps/api
   * adds a resolved plan name to the session response or the tech-lead names the
   * correct source.
   */
  readonly isSolo: boolean;
  readonly onOpenFeatureLane?: () => void;
  readonly onAskAboutUpgrading?: () => void;
}): React.JSX.Element => {
  const bubbleClass = `msg-bubble crew${answer.kind === "abstain" ? " abstain" : ""}`;
  const sourcesHeading = answer.kind === "answer" ? "Sources" : "What I checked";

  return (
    <div
      className={bubbleClass}
      lang={answer.language}
      data-testid="crew-answer"
      data-kind={answer.kind}
    >
      <div className="msg-who">
        <img src={glimIllustration} alt="" width={16} height={16} aria-hidden="true" />
        Glim, your crew
        <span
          className="chip lang-tag"
          aria-label={`Answered in ${LANGUAGE_NAMES[answer.language]}`}
        >
          {answer.language.toUpperCase()}
        </span>
      </div>
      {/* W2 fix (design-review): the abstain variant was distinguished from an
          ordinary answer only by a soft-panel surface swap and a low-contrast dashed
          border (fixed separately in app.css) - color/surface tone alone is not a
          reliable signal (color-not-only). This label+icon makes the "I don't know"
          framing explicit regardless of surface rendering. */}
      {answer.kind === "abstain" ? (
        <div className="msg-abstain-flag">
          <AbstainIcon />
          Couldn&apos;t ground this
        </div>
      ) : null}
      <p style={{ margin: 0 }}>{answer.answer}</p>
      <CitationList citations={answer.knows} heading={sourcesHeading} />
      {answer.assumes.length > 0 ? (
        <div className="msg-assuming">
          <div className="lbl">
            <AssumingIcon />
            Assuming
          </div>
          {answer.assumes.map((line, index) => (
            <p key={index} style={{ margin: 0 }}>
              {line}
            </p>
          ))}
        </div>
      ) : null}
      {answer.kind === "feature_request" ? (
        <div className="mt12">
          {isSolo ? (
            <div className="well pad">
              <p className="muted" style={{ marginBottom: 8 }}>
                Building new features is a Studio-and-up feature. You are on Solo.
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={onAskAboutUpgrading}
                >
                  Ask about upgrading
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" onClick={onOpenFeatureLane}>
              Open the feature lane
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};
